import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { OrgSettings } from "@induz/sdk";
import { useToast } from "components/Toaster/index";
import "./advancedSettings.scss";

const dedupeStringsPreserveOrder = (items) => {
  const seen = new Set();
  return (items || []).filter((item) => {
    const key = String(item || "").trim().toLowerCase();
    if (!key) return false;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const fileTypeOptions = dedupeStringsPreserveOrder([
  ".pdf", ".docx", ".txt", ".rtf", ".odt",
  ".xlsx", ".csv", ".pptx",
  ".jpg", ".png", ".gif", ".webp", ".svg",
  ".zip", ".rar", ".7z", ".tar", ".gz",
  ".mp3", ".mp4", ".json", ".log", ".bat", ".bin",
  ".idoc", ".isheet"
]);

const fileTypeToMimeTypeMap = {
  ".pdf": "application/pdf",
  ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".txt": "text/plain",
  ".rtf": "application/rtf",
  ".odt": "application/vnd.oasis.opendocument.text",
  ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".csv": "text/csv",
  ".pptx": "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ".jpg": "image/jpeg",
  ".png": "image/png",
  ".gif": "image/gif",
  ".webp": "image/webp",
  ".svg": "image/svg+xml",
  ".zip": "application/zip",
  ".rar": "application/vnd.rar",
  ".7z": "application/x-7z-compressed",
  ".tar": "application/x-tar",
  ".gz": "application/gzip",
  ".mp3": "audio/mpeg",
  ".mp4": "video/mp4",
  ".json": "application/json",
  ".log": "text/x-log",
  ".bat": "application/x-bat",
  ".bin": "application/octet-stream",
  ".idoc": "application/idoc",
  ".isheet": "application/isheet"
};

const mapFileTypesToMimeTypes = (fileTypes = []) =>
  dedupeStringsPreserveOrder(fileTypes).map((fileType) => {
    const normalized = (fileType || "").trim().toLowerCase();
    return fileTypeToMimeTypeMap[normalized] || fileType;
  });

const mimeTypeToFileTypeMap = Object.fromEntries(
  Object.entries(fileTypeToMimeTypeMap).map(([fileType, mimeType]) => [mimeType, fileType])
);

const mapMimeTypesToFileTypes = (mimeTypes = []) =>
  dedupeStringsPreserveOrder(mimeTypes).map((mimeType) => {
    const normalized = (mimeType || "").trim().toLowerCase();
    return mimeTypeToFileTypeMap[normalized] || mimeType;
  });

const toStringArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item || "").trim())
      .filter(Boolean);
  }
  if (value instanceof Set) {
    return Array.from(value)
      .map((item) => String(item || "").trim())
      .filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const getSummaryText = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) return "";
  return items.join(", ");
};

const getTriggerDisplayText = (items = [], emptyLabel) => {
  if (!Array.isArray(items) || items.length === 0) return emptyLabel;
  return getSummaryText(items);
};

const getTriggerTooltip = (items = [], emptyLabel, suffixLabel) => {
  if (!Array.isArray(items) || items.length === 0) return emptyLabel;
  return `${items.join(", ")} (${items.length} ${suffixLabel})`;
};

function AdvancedSettings() {
  const showToast = useToast();
  const [countryRegionsMap, setCountryRegionsMap] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedFilterOptions, setSelectedFilterOptions] = useState([]);
  const [topDropdownSearch, setTopDropdownSearch] = useState("");
  const dropdownRef = useRef(null);
  const dropdownTriggerRef = useRef(null);
  const dropdownMenuRef = useRef(null);
  const [topDropdownMenuStyle, setTopDropdownMenuStyle] = useState({});

  const [selectedAllowedCountries, setSelectedAllowedCountries] = useState([]);
  const [selectedDeniedCountries, setSelectedDeniedCountries] = useState([]);
  const [selectedAllowedRegions, setSelectedAllowedRegions] = useState([]);
  const [selectedDeniedRegions, setSelectedDeniedRegions] = useState([]);
  const [selectedAllowedFileTypes, setSelectedAllowedFileTypes] = useState([]);
  const [selectedDeniedFileTypes, setSelectedDeniedFileTypes] = useState([]);

  const [openCountryPicker, setOpenCountryPicker] = useState(null);
  const [countryPickerSearch, setCountryPickerSearch] = useState("");
  const [countryPickerMenuStyle, setCountryPickerMenuStyle] = useState({});
  const countryPickerMenuRef = useRef(null);
  const countryPickerTriggerRefs = useRef({});

  const [openRegionsPicker, setOpenRegionsPicker] = useState(null);
  const [regionsPickerSearch, setRegionsPickerSearch] = useState("");
  const [regionsPickerMenuStyle, setRegionsPickerMenuStyle] = useState({});
  const regionsPickerMenuRef = useRef(null);
  const regionsPickerTriggerRefs = useRef({});

  const [isAllowedFileTypesDropdownOpen, setIsAllowedFileTypesDropdownOpen] = useState(false);
  const [allowedFileTypesSearch, setAllowedFileTypesSearch] = useState("");
  const allowedFileTypesTriggerRef = useRef(null);
  const allowedFileTypesMenuRef = useRef(null);
  const [allowedFileTypesMenuStyle, setAllowedFileTypesMenuStyle] = useState({});

  const [isDeniedFileTypesDropdownOpen, setIsDeniedFileTypesDropdownOpen] = useState(false);
  const [deniedFileTypesSearch, setDeniedFileTypesSearch] = useState("");
  const deniedFileTypesTriggerRef = useRef(null);
  const deniedFileTypesMenuRef = useRef(null);
  const [deniedFileTypesMenuStyle, setDeniedFileTypesMenuStyle] = useState({});

  const availableCountries = useMemo(
    () => Object.keys(countryRegionsMap).sort((a, b) => a.localeCompare(b)),
    [countryRegionsMap]
  );

  const getRegionsForCountry = useCallback((country) => {
    if (!country) return [];
    return countryRegionsMap[country] || [];
  }, [countryRegionsMap]);

  const getSelectedRegionsForCountry = useCallback((country, selectedRegions = []) => {
    if (!country) return [];
    return getRegionsForCountry(country).filter((region) => selectedRegions.includes(region));
  }, [getRegionsForCountry]);

  const getAdaptiveMenuStyle = (rect, minWidth = 240, menuHeight = 260, forceDown = false) => {
    const viewportHeight = window.innerHeight || 0;
    const spaceBelow = Math.max(0, viewportHeight - rect.bottom);
    const spaceAbove = Math.max(0, rect.top);
    const shouldOpenUp = !forceDown && spaceBelow < menuHeight + 12 && spaceAbove > spaceBelow;
    return {
      position: "fixed",
      top: shouldOpenUp ? Math.max(8, rect.top - menuHeight - 6) : rect.bottom + 6,
      left: rect.left,
      width: Math.max(rect.width, minWidth),
      zIndex: 9999
    };
  };
  const filterOptions = [
    "Allowed Countries",
    "Denied Countries",
    "Allowed File Types",
    "Denied File Types"
  ];
  const filteredTopOptions = useMemo(
    () => filterOptions.filter((option) => option.toLowerCase().includes(topDropdownSearch.toLowerCase())),
    [topDropdownSearch]
  );

  useEffect(() => {
    const parseCsvRow = (line) => {
      const values = [];
      let current = "";
      let inQuotes = false;
      for (let i = 0; i < line.length; i += 1) {
        const char = line[i];
        const nextChar = line[i + 1];
        if (char === "\"") {
          if (inQuotes && nextChar === "\"") {
            current += "\"";
            i += 1;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === "," && !inQuotes) {
          values.push(current);
          current = "";
        } else {
          current += char;
        }
      }
      values.push(current);
      return values.map((v) => v.trim());
    };

    const loadCountryRegionsFromCsv = async () => {
      try {
        const response = await fetch("/data/countries_regions.csv");
        if (!response.ok) return;
        const csvText = await response.text();
        const lines = csvText.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
        if (lines.length <= 1) return;
        const parsedMap = {};
        for (let i = 1; i < lines.length; i += 1) {
          const row = parseCsvRow(lines[i]);
          if (row.length < 2) continue;
          const [country, region] = row;
          if (!country || !region) continue;
          if (!parsedMap[country]) parsedMap[country] = [];
          if (!parsedMap[country].includes(region)) parsedMap[country].push(region);
        }
        setCountryRegionsMap(parsedMap);
      } catch (error) {
        console.error("Failed to load countries_regions.csv:", error);
      }
    };

    loadCountryRegionsFromCsv();
  }, []);

  useEffect(() => {
    const loadOrgSettings = async () => {
      try {
        const settings = await new OrgSettings().getOrgSettings();
        const allowedCountries = toStringArray(settings.getAllowedCountries());
        const deniedCountries = toStringArray(settings.getDeniedCountries());
        const allowedRegions = toStringArray(settings.getAllowedRegions());
        const deniedRegions = toStringArray(settings.getDeniedRegions());
        const allowedFileTypes = toStringArray(settings.getAllowedFileTypes());
        const deniedFileTypes = toStringArray(settings.getDeniedFileTypes());

        setSelectedAllowedCountries(allowedCountries);
        setSelectedDeniedCountries(deniedCountries);
        setSelectedAllowedRegions(allowedRegions);
        setSelectedDeniedRegions(deniedRegions);
        setSelectedAllowedFileTypes(
          dedupeStringsPreserveOrder(mapMimeTypesToFileTypes(allowedFileTypes))
        );
        setSelectedDeniedFileTypes(
          dedupeStringsPreserveOrder(mapMimeTypesToFileTypes(deniedFileTypes))
        );

        const dynamicFilters = [];
        if (allowedCountries.length > 0) dynamicFilters.push("Allowed Countries");
        if (deniedCountries.length > 0) dynamicFilters.push("Denied Countries");
        if (allowedFileTypes.length > 0) dynamicFilters.push("Allowed File Types");
        if (deniedFileTypes.length > 0) dynamicFilters.push("Denied File Types");
        setSelectedFilterOptions(dynamicFilters);
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error("Failed to load org settings:", error);
        showToast(error?.message || "Failed to load advanced settings", "error");
      }
    };

    loadOrgSettings();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOnCountryPickerMenu = countryPickerMenuRef.current && countryPickerMenuRef.current.contains(event.target);
      const isOnRegionsPickerMenu = regionsPickerMenuRef.current && regionsPickerMenuRef.current.contains(event.target);
      const isOnAllowedTypesTrigger = allowedFileTypesTriggerRef.current && allowedFileTypesTriggerRef.current.contains(event.target);
      const isOnAllowedTypesMenu = allowedFileTypesMenuRef.current && allowedFileTypesMenuRef.current.contains(event.target);
      const isOnDeniedTypesTrigger = deniedFileTypesTriggerRef.current && deniedFileTypesTriggerRef.current.contains(event.target);
      const isOnDeniedTypesMenu = deniedFileTypesMenuRef.current && deniedFileTypesMenuRef.current.contains(event.target);
      const isOnCountryPickerTrigger = Boolean(event.target.closest(".CountryPickerTrigger"));
      const isOnRegionsPickerTrigger = Boolean(event.target.closest(".RegionsPickerTrigger"));
      const isOnTopDropdownMenu = dropdownMenuRef.current && dropdownMenuRef.current.contains(event.target);
      const isOnTopDropdownTrigger = dropdownTriggerRef.current && dropdownTriggerRef.current.contains(event.target);

      if (!isOnCountryPickerTrigger && !isOnCountryPickerMenu) {
        setOpenCountryPicker(null);
        setCountryPickerSearch("");
      }
      if (!isOnRegionsPickerTrigger && !isOnRegionsPickerMenu) {
        setOpenRegionsPicker(null);
        setRegionsPickerSearch("");
      }
      if (!isOnAllowedTypesTrigger && !isOnAllowedTypesMenu) setIsAllowedFileTypesDropdownOpen(false);
      if (!isOnDeniedTypesTrigger && !isOnDeniedTypesMenu) setIsDeniedFileTypesDropdownOpen(false);
      if (!isOnTopDropdownTrigger && !isOnTopDropdownMenu) setIsDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateTopDropdownMenuPosition = () => {
    if (!dropdownTriggerRef.current) return;
    setTopDropdownMenuStyle(
      getAdaptiveMenuStyle(dropdownTriggerRef.current.getBoundingClientRect(), 220, 260)
    );
  };

  useEffect(() => {
    if (!isDropdownOpen) return undefined;
    updateTopDropdownMenuPosition();
    window.addEventListener("resize", updateTopDropdownMenuPosition);
    window.addEventListener("scroll", updateTopDropdownMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateTopDropdownMenuPosition);
      window.removeEventListener("scroll", updateTopDropdownMenuPosition, true);
    };
  }, [isDropdownOpen]);

  const updateAllowedFileTypesMenuPosition = () => {
    if (!allowedFileTypesTriggerRef.current) return;
    setAllowedFileTypesMenuStyle(
      getAdaptiveMenuStyle(allowedFileTypesTriggerRef.current.getBoundingClientRect(), 260, 320)
    );
  };

  const updateDeniedFileTypesMenuPosition = () => {
    if (!deniedFileTypesTriggerRef.current) return;
    setDeniedFileTypesMenuStyle(
      getAdaptiveMenuStyle(deniedFileTypesTriggerRef.current.getBoundingClientRect(), 260, 320)
    );
  };

  useEffect(() => {
    if (!isAllowedFileTypesDropdownOpen) return undefined;
    updateAllowedFileTypesMenuPosition();
    window.addEventListener("resize", updateAllowedFileTypesMenuPosition);
    window.addEventListener("scroll", updateAllowedFileTypesMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateAllowedFileTypesMenuPosition);
      window.removeEventListener("scroll", updateAllowedFileTypesMenuPosition, true);
    };
  }, [isAllowedFileTypesDropdownOpen]);

  useEffect(() => {
    if (!isDeniedFileTypesDropdownOpen) return undefined;
    updateDeniedFileTypesMenuPosition();
    window.addEventListener("resize", updateDeniedFileTypesMenuPosition);
    window.addEventListener("scroll", updateDeniedFileTypesMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateDeniedFileTypesMenuPosition);
      window.removeEventListener("scroll", updateDeniedFileTypesMenuPosition, true);
    };
  }, [isDeniedFileTypesDropdownOpen]);

  const handleAllowedCountrySlotChange = (slotIndex, newCountry) => {
    setSelectedAllowedCountries((prev) => {
      if (prev.length === 0) return newCountry ? [newCountry] : [];
      const next = [...prev];
      next[slotIndex] = newCountry;
      return next;
    });
    setHasUnsavedChanges(true);
  };

  const handleDeniedCountrySlotChange = (slotIndex, newCountry) => {
    setSelectedDeniedCountries((prev) => {
      if (prev.length === 0) return newCountry ? [newCountry] : [];
      const next = [...prev];
      next[slotIndex] = newCountry;
      return next;
    });
    setHasUnsavedChanges(true);
  };

  const handleAddAllowedCountrySlot = () => setSelectedAllowedCountries((prev) => [...prev, ""]);
  const handleAddDeniedCountrySlot = () => setSelectedDeniedCountries((prev) => [...prev, ""]);

  const toggleAllowedRegion = (region) => {
    if (selectedDeniedRegions.includes(region)) return;
    setSelectedAllowedRegions((prev) => (prev.includes(region) ? prev.filter((item) => item !== region) : [...prev, region]));
    setHasUnsavedChanges(true);
  };
  const toggleDeniedRegion = (region) => {
    if (selectedAllowedRegions.includes(region)) return;
    setSelectedDeniedRegions((prev) => (prev.includes(region) ? prev.filter((item) => item !== region) : [...prev, region]));
    setHasUnsavedChanges(true);
  };
  const toggleAllowedFileType = (type) => {
    if (selectedDeniedFileTypes.includes(type)) return;
    setSelectedAllowedFileTypes((prev) => (prev.includes(type) ? prev.filter((item) => item !== type) : [...prev, type]));
    setHasUnsavedChanges(true);
  };
  const toggleDeniedFileType = (type) => {
    if (selectedAllowedFileTypes.includes(type)) return;
    setSelectedDeniedFileTypes((prev) => (prev.includes(type) ? prev.filter((item) => item !== type) : [...prev, type]));
    setHasUnsavedChanges(true);
  };

  const openCountryPickerFor = (kind, idx) => {
    const key = `${kind}-${idx}`;
    const el = countryPickerTriggerRefs.current[key];
    if (!el) return;
    setCountryPickerMenuStyle(getAdaptiveMenuStyle(el.getBoundingClientRect(), 260, 320, true));
    setOpenCountryPicker({ kind, idx });
  };

  const toggleFilterOption = (option) => {
    setSelectedFilterOptions((prev) => {
      const isRemoving = prev.includes(option);
      const next = isRemoving ? prev.filter((item) => item !== option) : [...prev, option];
      if (isRemoving) {
        if (option === "Allowed Countries") {
          setSelectedAllowedCountries([]);
          setSelectedAllowedRegions([]);
        }
        if (option === "Denied Countries") {
          setSelectedDeniedCountries([]);
          setSelectedDeniedRegions([]);
        }
        if (option === "Allowed File Types") setSelectedAllowedFileTypes([]);
        if (option === "Denied File Types") setSelectedDeniedFileTypes([]);
      }
      return next;
    });
    setHasUnsavedChanges(true);
  };

  const openRegionsPickerFor = (kind, idx) => {
    const key = `${kind}-${idx}`;
    const el = regionsPickerTriggerRefs.current[key];
    if (!el) return;
    setRegionsPickerMenuStyle(getAdaptiveMenuStyle(el.getBoundingClientRect(), 300, 320, true));
    setOpenRegionsPicker({ kind, idx });
  };

  const handleSaveAdvancedSettings = async () => {
    setIsSaving(true);
    try {
      const orgSettings = new OrgSettings();
      orgSettings.setAllowedCountries(selectedAllowedCountries.filter(Boolean));
      orgSettings.setDeniedCountries(selectedDeniedCountries.filter(Boolean));
      orgSettings.setAllowedRegions(selectedAllowedRegions);
      orgSettings.setDeniedRegions(selectedDeniedRegions);
      orgSettings.setAllowedFileTypes(mapFileTypesToMimeTypes(selectedAllowedFileTypes));
      orgSettings.setDeniedFileTypes(mapFileTypesToMimeTypes(selectedDeniedFileTypes));
      await orgSettings.updateOrgSettings();
      setHasUnsavedChanges(false);
      showToast("Advanced settings saved successfully", "success");
    } catch (error) {
      console.error("Error saving advanced settings:", error);
      showToast(error?.message || "Failed to save advanced settings", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const allowedCountrySlotsForUI = selectedAllowedCountries.length > 0 ? selectedAllowedCountries : [""];
  const deniedCountrySlotsForUI = selectedDeniedCountries.length > 0 ? selectedDeniedCountries : [""];
  const filteredAllowedFileTypes = fileTypeOptions.filter((type) => type.toLowerCase().includes(allowedFileTypesSearch.toLowerCase()));
  const filteredDeniedFileTypes = fileTypeOptions.filter((type) => type.toLowerCase().includes(deniedFileTypesSearch.toLowerCase()));

  return (
    <div className="AdminAdvancedSettings">
      <div className="AdvancedTopControls">
        <div className="MultiSelectDropdown" ref={dropdownRef}>
          <button
            type="button"
            ref={dropdownTriggerRef}
            className={`DropdownTrigger TopFilterTrigger ${isDropdownOpen ? "open" : ""} ${selectedFilterOptions.length > 0 ? "hasValue" : ""}`}
            onClick={() => setIsDropdownOpen((prev) => !prev)}
          >
            {selectedFilterOptions.length > 0 ? `${selectedFilterOptions.length} selected` : "Select advanced settings"}
          </button>
          {isDropdownOpen && createPortal(
            <div className="DropdownMenu DropdownMenuPortal" ref={dropdownMenuRef} style={topDropdownMenuStyle}>
              <input
                type="text"
                className="DropdownSearchInput"
                placeholder="Search options..."
                value={topDropdownSearch}
                onChange={(e) => setTopDropdownSearch(e.target.value)}
              />
              {filteredTopOptions.map((option) => (
                <label key={option} className="DropdownOption">
                  <input
                    type="checkbox"
                    checked={selectedFilterOptions.includes(option)}
                    onChange={() => toggleFilterOption(option)}
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>,
            document.body
          )}
        </div>
        <button
          type="button"
          className={`PrimarySm ${hasUnsavedChanges ? "hasChanges" : ""}`}
          disabled={!hasUnsavedChanges || isSaving}
          onClick={handleSaveAdvancedSettings}
        >
          {isSaving ? "Saving..." : "Save Advanced Settings"}
        </button>
      </div>

      <div className="AdvancedSettingsPanel">
          {selectedFilterOptions.includes("Allowed Countries") && (
          <div className="AdvancedSettingsRow">
            <div className="NameColumn">Allowed Countries</div>
            <div className="ValueColumn">
              <div className="CountriesRegionsScroller CountriesRegionsScrollerAllowed">
                <div className="CountriesRegionCards">
                  {allowedCountrySlotsForUI.map((slotCountry, slotIdx) => {
                    const deniedSet = new Set(selectedDeniedCountries.filter(Boolean));
                    const otherAllowedSet = new Set(selectedAllowedCountries.filter((c, idx) => Boolean(c) && idx !== slotIdx));
                    const allowedCountryOptionsBase = availableCountries.filter((country) => !deniedSet.has(country) && !otherAllowedSet.has(country));
                    const allowedCountryOptions = slotCountry && !allowedCountryOptionsBase.includes(slotCountry)
                      ? [slotCountry, ...allowedCountryOptionsBase]
                      : allowedCountryOptionsBase;
                    return (
                      <div className="CountryRegionRow" key={`allowed-country-slot-${slotIdx}`}>
                        <div className="CountrySlotCard">
                          <div className="CountrySlotTitle">Allowed country</div>
                          <button
                            type="button"
                            className="DropdownTrigger CountryPickerTrigger"
                            ref={(el) => { countryPickerTriggerRefs.current[`allowed-${slotIdx}`] = el; }}
                            onClick={() => {
                              if (openCountryPicker?.kind === "allowed" && openCountryPicker?.idx === slotIdx) {
                                setOpenCountryPicker(null);
                                setCountryPickerSearch("");
                                return;
                              }
                              setCountryPickerSearch("");
                              openCountryPickerFor("allowed", slotIdx);
                            }}
                          >
                            {slotCountry || "Select country"}
                          </button>
                        </div>
                        <div className="RegionsSlotCard">
                          <div className="RegionsSlotTitle">Allowed regions</div>
                          {!slotCountry ? <div className="RegionsSlotEmpty" /> : (
                            <button
                              type="button"
                              className="DropdownTrigger RegionsPickerTrigger"
                              ref={(el) => { regionsPickerTriggerRefs.current[`allowed-${slotIdx}`] = el; }}
                              title={getTriggerTooltip(
                                getSelectedRegionsForCountry(slotCountry, selectedAllowedRegions),
                                "Select regions",
                                "regions selected"
                              )}
                              onClick={() => {
                                if (openRegionsPicker?.kind === "allowed" && openRegionsPicker?.idx === slotIdx) {
                                  setOpenRegionsPicker(null);
                                  setRegionsPickerSearch("");
                                  return;
                                }
                                setRegionsPickerSearch("");
                                openRegionsPickerFor("allowed", slotIdx);
                              }}
                            >
                              {getTriggerDisplayText(
                                getSelectedRegionsForCountry(slotCountry, selectedAllowedRegions),
                                "Select regions"
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button type="button" className="AddCountrySlotBtn" onClick={handleAddAllowedCountrySlot}>+1 Add country</button>
              </div>
            </div>
          </div>
          )}

          {selectedFilterOptions.includes("Denied Countries") && (
          <div className="AdvancedSettingsRow">
            <div className="NameColumn">Denied Countries</div>
            <div className="ValueColumn">
              <div className="CountriesRegionsScroller CountriesRegionsScrollerDenied">
                <div className="CountriesRegionCards">
                  {deniedCountrySlotsForUI.map((slotCountry, slotIdx) => {
                    const allowedSet = new Set(selectedAllowedCountries.filter(Boolean));
                    const otherDeniedSet = new Set(selectedDeniedCountries.filter((c, idx) => Boolean(c) && idx !== slotIdx));
                    const deniedCountryOptionsBase = availableCountries.filter((country) => !allowedSet.has(country) && !otherDeniedSet.has(country));
                    const deniedCountryOptions = slotCountry && !deniedCountryOptionsBase.includes(slotCountry)
                      ? [slotCountry, ...deniedCountryOptionsBase]
                      : deniedCountryOptionsBase;
                    return (
                      <div className="CountryRegionRow" key={`denied-country-slot-${slotIdx}`}>
                        <div className="CountrySlotCard">
                          <div className="CountrySlotTitle">Denied country</div>
                          <button
                            type="button"
                            className="DropdownTrigger CountryPickerTrigger"
                            ref={(el) => { countryPickerTriggerRefs.current[`denied-${slotIdx}`] = el; }}
                            onClick={() => {
                              if (openCountryPicker?.kind === "denied" && openCountryPicker?.idx === slotIdx) {
                                setOpenCountryPicker(null);
                                setCountryPickerSearch("");
                                return;
                              }
                              setCountryPickerSearch("");
                              openCountryPickerFor("denied", slotIdx);
                            }}
                          >
                            {slotCountry || "Select country"}
                          </button>
                        </div>
                        <div className="RegionsSlotCard">
                          <div className="RegionsSlotTitle">Denied regions</div>
                          {!slotCountry ? <div className="RegionsSlotEmpty" /> : (
                            <button
                              type="button"
                              className="DropdownTrigger RegionsPickerTrigger"
                              ref={(el) => { regionsPickerTriggerRefs.current[`denied-${slotIdx}`] = el; }}
                              title={getTriggerTooltip(
                                getSelectedRegionsForCountry(slotCountry, selectedDeniedRegions),
                                "Select regions",
                                "regions selected"
                              )}
                              onClick={() => {
                                if (openRegionsPicker?.kind === "denied" && openRegionsPicker?.idx === slotIdx) {
                                  setOpenRegionsPicker(null);
                                  setRegionsPickerSearch("");
                                  return;
                                }
                                setRegionsPickerSearch("");
                                openRegionsPickerFor("denied", slotIdx);
                              }}
                            >
                              {getTriggerDisplayText(
                                getSelectedRegionsForCountry(slotCountry, selectedDeniedRegions),
                                "Select regions"
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button type="button" className="AddCountrySlotBtn" onClick={handleAddDeniedCountrySlot}>+1 Add country</button>
              </div>
            </div>
          </div>
          )}

          {selectedFilterOptions.includes("Allowed File Types") && (
          <div className="AdvancedSettingsRow">
            <div className="NameColumn">Allowed File Types</div>
            <div className="ValueColumn FileTypeValueColumn">
              <button
                type="button"
                ref={allowedFileTypesTriggerRef}
                className={`DropdownTrigger FileTypeTrigger ${isAllowedFileTypesDropdownOpen ? "open" : ""}`}
                title={getTriggerTooltip(selectedAllowedFileTypes, "Select file types", "selected")}
                onClick={() => setIsAllowedFileTypesDropdownOpen((prev) => !prev)}
              >
                {getTriggerDisplayText(selectedAllowedFileTypes, "Select file types")}
              </button>
            </div>
          </div>
          )}

          {selectedFilterOptions.includes("Denied File Types") && (
          <div className="AdvancedSettingsRow">
            <div className="NameColumn">Denied File Types</div>
            <div className="ValueColumn FileTypeValueColumn">
              <button
                type="button"
                ref={deniedFileTypesTriggerRef}
                className={`DropdownTrigger FileTypeTrigger ${isDeniedFileTypesDropdownOpen ? "open" : ""}`}
                title={getTriggerTooltip(selectedDeniedFileTypes, "Select file types", "selected")}
                onClick={() => setIsDeniedFileTypesDropdownOpen((prev) => !prev)}
              >
                {getTriggerDisplayText(selectedDeniedFileTypes, "Select file types")}
              </button>
            </div>
          </div>
          )}
      </div>

      {openCountryPicker && createPortal(
        <div className="DropdownMenu DropdownMenuPortal CountryPickerMenu" ref={countryPickerMenuRef} style={countryPickerMenuStyle}>
          <input
            type="text"
            className="DropdownSearchInput"
            placeholder="Search countries..."
            value={countryPickerSearch}
            onChange={(e) => setCountryPickerSearch(e.target.value)}
          />
          {(() => {
            const { kind, idx } = openCountryPicker;
            const slotCountry = kind === "allowed" ? (allowedCountrySlotsForUI[idx] || "") : (deniedCountrySlotsForUI[idx] || "");
            const forbiddenSet = new Set(kind === "allowed" ? selectedDeniedCountries.filter(Boolean) : selectedAllowedCountries.filter(Boolean));
            const ownOtherSet = new Set((kind === "allowed" ? selectedAllowedCountries : selectedDeniedCountries).filter((c, i) => Boolean(c) && i !== idx));
            const baseOptions = availableCountries.filter((country) => {
              if (forbiddenSet.has(country) || ownOtherSet.has(country)) return false;
              return !countryPickerSearch || country.toLowerCase().includes(countryPickerSearch.toLowerCase());
            });
            const options = slotCountry && !baseOptions.includes(slotCountry) ? [slotCountry, ...baseOptions] : baseOptions;
            return (
              <>
                <label className="DropdownOption">
                  <input
                    type="radio"
                    name={`country-${kind}-${idx}`}
                    checked={slotCountry === ""}
                    onChange={() => {
                      if (kind === "allowed") handleAllowedCountrySlotChange(idx, "");
                      else handleDeniedCountrySlotChange(idx, "");
                      setOpenCountryPicker(null);
                    }}
                  />
                  <span>Clear selection</span>
                </label>
                {options.length > 0 ? options.map((country) => (
                  <label key={country} className="DropdownOption">
                    <input
                      type="radio"
                      name={`country-${kind}-${idx}`}
                      checked={slotCountry === country}
                      onChange={() => {
                        if (kind === "allowed") handleAllowedCountrySlotChange(idx, country);
                        else handleDeniedCountrySlotChange(idx, country);
                        setOpenCountryPicker(null);
                      }}
                    />
                    <span>{country}</span>
                  </label>
                )) : (
                  <div className="DropdownOption"><span>No countries found</span></div>
                )}
              </>
            );
          })()}
        </div>,
        document.body
      )}

      {openRegionsPicker && createPortal(
        <div className="DropdownMenu DropdownMenuPortal RegionsPickerMenu" ref={regionsPickerMenuRef} style={regionsPickerMenuStyle}>
          <input
            type="text"
            className="DropdownSearchInput"
            placeholder="Search regions..."
            value={regionsPickerSearch}
            onChange={(e) => setRegionsPickerSearch(e.target.value)}
          />
          {(() => {
            const { kind, idx } = openRegionsPicker;
            const slotCountry = kind === "allowed" ? (allowedCountrySlotsForUI[idx] || "") : (deniedCountrySlotsForUI[idx] || "");
            const regions = slotCountry ? getRegionsForCountry(slotCountry) : [];
            const filtered = regions.filter((region) => region.toLowerCase().includes(regionsPickerSearch.toLowerCase()));
            if (!slotCountry) return <div className="DropdownOption"><span>Select country first</span></div>;
            if (filtered.length === 0) return <div className="DropdownOption"><span>No regions found</span></div>;
            return filtered.map((region) => (
              <label key={region} className="DropdownOption">
                <input
                  type="checkbox"
                  checked={kind === "allowed" ? selectedAllowedRegions.includes(region) : selectedDeniedRegions.includes(region)}
                  disabled={kind === "allowed" ? selectedDeniedRegions.includes(region) : selectedAllowedRegions.includes(region)}
                  onChange={() => {
                    if (kind === "allowed") toggleAllowedRegion(region);
                    else toggleDeniedRegion(region);
                  }}
                />
                <span>{region}</span>
              </label>
            ));
          })()}
        </div>,
        document.body
      )}

      {isAllowedFileTypesDropdownOpen && createPortal(
        <div className="DropdownMenu DropdownMenuPortal" ref={allowedFileTypesMenuRef} style={allowedFileTypesMenuStyle}>
          <input
            type="text"
            className="DropdownSearchInput"
            placeholder="Search file types..."
            value={allowedFileTypesSearch}
            onChange={(e) => setAllowedFileTypesSearch(e.target.value)}
          />
          {filteredAllowedFileTypes.map((type) => (
            <label key={type} className="DropdownOption">
              <input
                type="checkbox"
                checked={selectedAllowedFileTypes.includes(type)}
                disabled={selectedDeniedFileTypes.includes(type)}
                onChange={() => toggleAllowedFileType(type)}
              />
              <span>{type}</span>
            </label>
          ))}
        </div>,
        document.body
      )}

      {isDeniedFileTypesDropdownOpen && createPortal(
        <div className="DropdownMenu DropdownMenuPortal" ref={deniedFileTypesMenuRef} style={deniedFileTypesMenuStyle}>
          <input
            type="text"
            className="DropdownSearchInput"
            placeholder="Search file types..."
            value={deniedFileTypesSearch}
            onChange={(e) => setDeniedFileTypesSearch(e.target.value)}
          />
          {filteredDeniedFileTypes.map((type) => (
            <label key={type} className="DropdownOption">
              <input
                type="checkbox"
                checked={selectedDeniedFileTypes.includes(type)}
                disabled={selectedAllowedFileTypes.includes(type)}
                onChange={() => toggleDeniedFileType(type)}
              />
              <span>{type}</span>
            </label>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

export default AdvancedSettings;
